/*
 * The proctoring review's rules: what is flagged, how serious it is, and how
 * the room words it. A plain-JS port of unberry-4.0
 * `web/src/server/reports/proctoring.ts` and `web/src/lib/proctoring.ts`,
 * plus the room's headline and `narrate()` from
 * `web/src/components/RoundReport/ProctorReview.tsx`.
 *
 * Two halves, kept apart as in 4.0:
 *  - `proctorReport(events)` decides every flag from a sitting's events, and
 *    `proctorFeed(report)` lists every instance with its severity. This is
 *    the 4.0 event model; real captured data is mapped straight to feed
 *    items by `fromTimeline.js`, and both produce the same FeedItem shape.
 *  - `feedCounts`, `integrityOf`, `reviewHeadline`, `narrate` and `ringBand`
 *    read any feed, whichever half made it.
 *
 * Fixed on the way over from 4.0 (each has a test):
 *  - narrate's list: "A.", "A and b.", "A, b, and c." (4.0 wrote "A,  and b.");
 *  - a flagged typing burst's detail is only where it went (4.0 repeated the figures);
 *  - a long switch away is flagged only when tab switching is monitored;
 *  - each flag carries `ref: {kind, index}` and the feed finds it by ref, so
 *    two events of one segment in the same second keep their own words;
 *  - the integrity figure and the counts leave out the recording's own items
 *    and experimental items, so they total the same as the review lists;
 *  - audio is never "a second voice": there is no speaker detection, only
 *    speech heard outside the answers.
 */
import { span } from "./format";

export { clockOf, compact, parseClock, span } from "./format";

/* ---- the data shapes ---- */

/**
 * @typedef {"overall"|"image"|"audio"|"screen"|"video"|"ai"} SegmentKey
 * @typedef {"clear"|"review"|"critical"} Severity
 * @typedef {"session"|"capture"|"profile"|"tab"|"tabcount"|"fullscreen"|"clipboard"|"audio"|"quote"|"screen"|"video"|"typing"|"passage"|"integrity"} FeedKind
 * @typedef {"match"|"none"|"second"|"different"} CaptureResult
 * @typedef {"shared"|"display"|"focus"|"screenshot"} ScreenKind
 */

/**
 * What the round was set to watch. `audio`, `screen` and `ai` left out mean on.
 * @typedef {Object} ProctorSettings
 * @property {boolean} tab       tab switches are monitored
 * @property {number} tabLimit   switches allowed before each one is flagged
 * @property {boolean} full      full screen is required
 * @property {boolean} video     the sitting is recorded
 * @property {boolean} image     camera captures are analysed (PositionConfig.aiProctoring)
 * @property {boolean} copy      copy and paste are blocked and attempts recorded
 * @property {boolean} [audio]
 * @property {boolean} [screen]
 * @property {boolean} [ai]      answers are checked for AI help
 */

/**
 * A sitting's events, in the 4.0 model. Every time is in seconds from the start.
 * @typedef {Object} ProctorEvents
 * @property {ProctorSettings} settings
 * @property {number} length   how long the round ran
 * @property {number} every    how often a capture is taken
 * @property {Array<{at: number, away: number}>} tabs
 * @property {Array<{at: number, back: number}>} fullScreen
 * @property {Array<{at: number, result: CaptureResult}>} captures
 * @property {Array<{at: number, dur: number, kind: "voice"|"speech"}>} audio
 *   `voice` is speech the rules treat as possible outside help; `speech` is speech outside the answers
 * @property {Array<{at: number, kind: ScreenKind, dur?: number}>} screen
 * @property {Array<{at: number, kind: "copy"|"paste", into: string}>} clipboard
 * @property {Array<{at: number, chars: number, secs: number, keys: number, into: string, passage?: number}>} typing
 * @property {Array<{i: number, text: string, likelihood: number}>} passages
 * @property {Array<{key: string, from: number, to: number, name: string}>} [parts]
 */

/**
 * One thing flagged: where it is, when, what it was, and the event it came from.
 * @typedef {Object} Flag
 * @property {Exclude<SegmentKey, "video">} seg
 * @property {number} at
 * @property {string} what
 * @property {{kind: FeedKind, index: number}} ref  the event's kind and its index in the events list
 */

/**
 * One instance in the feed. A flagged one plays from LEAD seconds before.
 * @typedef {Object} FeedItem
 * @property {string} id
 * @property {string} key          stable across loads
 * @property {number} at           seconds from the start of the round
 * @property {number} [dur]
 * @property {FeedKind} kind
 * @property {SegmentKey} seg
 * @property {Severity} severity
 * @property {boolean} flagged     severity is not clear
 * @property {string} what
 * @property {string|null} detail  a second line
 * @property {string} [capture]    CaptureResult, "reference", an image-AI verdict type, or "unverified"
 * @property {number} [question]   the question's number, when the instance belongs to one
 * @property {string} [questionKey]
 * @property {string} [note]       a caveat shown with the item
 * @property {boolean} [experimental]  listed, but left out of the counts and the integrity figure
 * @property {boolean} [approx]    placed approximately on the clock
 * @property {string[]} [signals]  for integrity items: which signals flagged it ("aiGenerated", "readAloud")
 * @property {boolean} [snapshot]  for screen items: a screen snapshot rather than a screen event
 */

/* ---- the rules ---- */

/** A switch away from the round longer than this is flagged even inside the limit (when tab switches are monitored). */
export const LONG_AWAY = 60;
/** Out of full screen for longer than this is flagged. */
export const FULL_SCREEN_GRACE = 10;
/** A sound shorter than this is too short to be sure of and is not flagged. */
export const AUDIO_MIN = 3;
/** The window losing focus for longer than this is flagged. */
export const FOCUS_GRACE = 5;
/** Blocked copy and paste attempts up to this many in a round are let pass; each one after is flagged. */
export const CLIP_ALLOWED = 2;
/** Writing of at least TYPED_MIN characters does not look typed when it comes faster than TYPED_CPS characters a
    second, faster than TYPED_FACTOR times the person's own speed in the rest of the round, or with keystrokes for
    fewer than KEYS_SHARE of its characters. */
export const TYPED_MIN = 60;
export const TYPED_CPS = 12;
export const TYPED_FACTOR = 3;
export const KEYS_SHARE = 0.5;
/** A passage (or an answer) whose likelihood of AI help is this or higher is flagged. */
export const AI_LIKELY = 0.8;
/** A flagged moment plays from this many seconds before it. */
export const LEAD = 5;

/** The segments the proctoring report is read in, each with what it holds. */
export const SEGMENT_NAMES = [
  { k: "overall", n: "Overall analysis", holds: "captures, tab switches and session restarts" },
  { k: "image", n: "Image fingerprint", holds: "the reference image and every capture" },
  { k: "audio", n: "Audio fingerprint", holds: "speech outside the answers and quotes from the audio review" },
  { k: "screen", n: "Screen fingerprint", holds: "sharing, displays, screenshots and focus" },
  { k: "video", n: "Video", holds: "the recording, its length and the face in frame" },
  { k: "ai", n: "AI fingerprint", holds: "response timing pattern, AI-style phrasing and answer integrity" },
];

/** The kinds of instance, in the order they sort at the same moment. The 4.0 nine keep their order; the rest are the real data's. */
export const FEED_KINDS = [
  { k: "session", n: "Session restarts", seg: "overall" },
  { k: "capture", n: "Captures", seg: "image" },
  { k: "profile", n: "Profile check", seg: "image" },
  { k: "tab", n: "Tab switches", seg: "overall" },
  { k: "tabcount", n: "Tab switches by question", seg: "overall" },
  { k: "fullscreen", n: "Full screen", seg: "overall" },
  { k: "clipboard", n: "Copy and paste", seg: "overall" },
  { k: "audio", n: "Audio", seg: "audio" },
  { k: "quote", n: "Audio review", seg: "audio" },
  { k: "screen", n: "Screen", seg: "screen" },
  { k: "video", n: "Recording", seg: "video" },
  { k: "typing", n: "Response timing pattern", seg: "ai" },
  { k: "passage", n: "AI-style phrasing", seg: "ai" },
  { k: "integrity", n: "Answer integrity", seg: "ai" },
];

export const SEVERITY_NAME = { clear: "Clear", review: "To review", critical: "Critical" };

const KIND_SEG = FEED_KINDS.reduce((acc, x) => ({ ...acc, [x.k]: x.seg }), {});
const KIND_ORDER = FEED_KINDS.reduce((acc, x, i) => ({ ...acc, [x.k]: i }), {});
/** The segment a kind of instance belongs to. */
export const segOf = (kind) => KIND_SEG[kind];

/** Compares two strings by code unit, the same in every browser (localeCompare is not). */
export const byText = (a, b) => {
  const x = String(a);
  const y = String(b);
  return x < y ? -1 : x > y ? 1 : 0;
};

/** At the same moment a reference image lists before the captures checked against it. */
const refFirst = (x) => (x.capture === "reference" ? 0 : 1);
/** Sorts a feed by time, then by kind, then by key, so the same data always lists the same way. */
export const byFeedOrder = (a, b) => a.at - b.at || KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || refFirst(a) - refFirst(b) || byText(a.key, b.key);

const CAPTURE_WORD = {
  none: "No face in view",
  second: "A second face in view",
  different: "Not the person in the reference image",
};
const SCREEN_WORD = {
  shared: "Screen shared",
  display: "A second display connected",
  focus: "The window lost focus",
  screenshot: "A screenshot attempted",
};
/** No speaker detection exists, so audio is described by when speech was heard, never by whose voice it was. */
const AUDIO_WORD = { voice: "Possible outside help", speech: "Speech outside answers" };

const count = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const ordinal = (n) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? "th" : ["th", "st", "nd", "rd"][n % 10] || "th"}`;
const r1 = (n) => Math.round(n * 10) / 10;

/**
 * What a sitting was watched for, in words, from the round's settings.
 * @param {ProctorSettings} S
 * @returns {string[]}
 */
export function monitored(S) {
  return [
    S.tab ? `Tab switches (${S.tabLimit} allowed)` : null,
    S.full ? "Full screen" : null,
    S.image ? "Face captures" : null,
    S.audio !== false ? "Audio" : null,
    S.screen !== false ? "Screen" : null,
    S.video ? "Video" : null,
    S.copy ? "Copy and paste blocked, attempts recorded" : null,
    S.ai !== false ? "Typing" : null,
    S.ai !== false ? "AI-written text" : null,
  ].filter(Boolean);
}

const cpsOf = (xs) => {
  const c = xs.reduce((a, t) => a + t.chars, 0);
  const s = xs.reduce((a, t) => a + t.secs, 0);
  return s ? c / s : null;
};

/**
 * Why a stretch of writing does not look typed, or null when it does.
 * @returns {string|null}
 */
export function untyped(t, all) {
  if (t.chars < TYPED_MIN || !t.secs) return null;
  const cps = t.chars / t.secs;
  const own = cpsOf(all.filter((x) => x !== t));
  if (t.keys < t.chars * KEYS_SHARE) return `${t.chars} characters appeared from ${t.keys} keystrokes`;
  if (cps > TYPED_CPS) return `${t.chars} characters in ${span(t.secs)}, ${Math.round(cps)} a second, faster than anyone types`;
  if (own && cps > own * TYPED_FACTOR) return `${t.chars} characters in ${span(t.secs)}, ${Math.round(cps)} a second against their own ${r1(own)}`;
  return null;
}

/** Each paragraph of the written answer with its likelihood of AI help and any writing in it that did not look typed. */
export function aiPassages(ev) {
  return ev.passages.map((p) => {
    const hit = ev.typing.find((t) => t.passage === p.i && untyped(t, ev.typing));
    return {
      i: p.i,
      text: p.text,
      likelihood: p.likelihood,
      likely: p.likelihood >= AI_LIKELY,
      burst: hit ? { at: hit.at, chars: hit.chars, secs: hit.secs, keys: hit.keys, why: untyped(hit, ev.typing) } : null,
    };
  });
}

/**
 * Every flag from the events, and each segment's verdict.
 * @param {ProctorEvents} ev
 */
export function proctorReport(ev) {
  const S = ev.settings;
  const byTime = (a, b) => a.at - b.at;

  /* overall: tab switches beyond the limit, or any one long enough to matter, both only when tab switches are
     monitored; full-screen exits past the grace; blocked copy and paste attempts past the allowance */
  const overall = [];
  ev.tabs.forEach((t, i) => {
    const over = S.tab && i >= S.tabLimit;
    const long = S.tab && t.away > LONG_AWAY;
    if (over || long) {
      overall.push({ seg: "overall", at: t.at, what: `Tab switch ${i + 1}${over ? `, beyond the ${S.tabLimit} allowed` : ""}, away ${span(t.away)}`, ref: { kind: "tab", index: i } });
    }
  });
  if (S.full) ev.fullScreen.forEach((f, i) => {
    if (f.back > FULL_SCREEN_GRACE) overall.push({ seg: "overall", at: f.at, what: `Left full screen for ${span(f.back)}`, ref: { kind: "fullscreen", index: i } });
  });
  if (S.copy) ev.clipboard.forEach((c, i) => {
    if (i >= CLIP_ALLOWED) overall.push({ seg: "overall", at: c.at, what: `A ${c.kind} attempt blocked in ${c.into}, ${ordinal(i + 1)} in the round (${CLIP_ALLOWED} let pass)`, ref: { kind: "clipboard", index: i } });
  });
  overall.sort(byTime);
  const away = ev.tabs.reduce((a, t) => a + t.away, 0);
  const clip = S.copy && ev.clipboard.length ? `; ${count(ev.clipboard.length, "copy or paste attempt", "copy or paste attempts")} blocked` : "";
  const overallVerdict = !ev.tabs.length && !ev.fullScreen.length && !clip
    ? "Stayed on the round throughout"
    : `${count(ev.tabs.length, "tab switch", "tab switches")} (${S.tabLimit} allowed), away ${span(away)} in all; ${count(ev.fullScreen.length, "full-screen exit", "full-screen exits")}${clip}`;

  /* image: every capture that is not the person in the reference image */
  const image = [];
  if (S.image) ev.captures.forEach((c, i) => {
    if (c.result !== "match") image.push({ seg: "image", at: c.at, what: CAPTURE_WORD[c.result], ref: { kind: "capture", index: i } });
  });
  const matched = ev.captures.filter((c) => c.result === "match").length;
  const imageVerdict = `Face matched in ${matched} of ${ev.captures.length} captures`;

  /* audio: speech outside the answers long enough to be sure of */
  const audio = [];
  ev.audio.forEach((a, i) => {
    if (a.dur >= AUDIO_MIN) audio.push({ seg: "audio", at: a.at, what: `${AUDIO_WORD[a.kind]} for ${span(a.dur)}`, ref: { kind: "audio", index: i } });
  });
  const short = ev.audio.length - audio.length;
  const audioVerdict = !ev.audio.length
    ? "No speech outside answers"
    : `${count(audio.length, "moment", "moments")} of speech outside answers${short ? `; ${count(short, "sound", "sounds")} under ${AUDIO_MIN} s not flagged` : ""}`;

  /* screen: sharing, a second display and screenshots always; focus lost past the grace */
  const screen = [];
  ev.screen.forEach((e, i) => {
    if (e.kind !== "focus" || (e.dur || 0) > FOCUS_GRACE) {
      screen.push({ seg: "screen", at: e.at, what: e.kind === "focus" ? `${SCREEN_WORD.focus} for ${span(e.dur || 0)}` : SCREEN_WORD[e.kind], ref: { kind: "screen", index: i } });
    }
  });
  const screenVerdict = !ev.screen.length ? "One display, not shared, no screenshots" : `${count(screen.length, "screen event", "screen events")} flagged`;

  /* AI: writing that does not look typed, passages likely written with help */
  const ai = [];
  ev.typing.forEach((t, i) => {
    const why = untyped(t, ev.typing);
    if (why) ai.push({ seg: "ai", at: t.at, what: `Did not look typed: ${why}`, ref: { kind: "typing", index: i } });
  });
  ev.passages.forEach((p, i) => {
    if (p.likelihood >= AI_LIKELY) {
      ai.push({ seg: "ai", at: ev.length, what: `Paragraph ${p.i + 1} of the case study likely written with AI help (${Math.round(p.likelihood * 100)} in 100)`, ref: { kind: "passage", index: i } });
    }
  });
  const aiVerdict = !ai.length ? "Written as typed, nothing likely written with AI help" : `${count(ai.length, "sign", "signs")} of text not written in the round`;

  const all = [...overall, ...image, ...audio, ...screen, ...ai].sort(byTime);
  const videoVerdict = S.video
    ? (all.length ? `${count(all.length, "moment", "moments")} marked on the recording` : "Recorded throughout, nothing to mark")
    : "Not recorded";
  const segs = {
    overall: [overallVerdict, overall],
    image: [imageVerdict, image],
    audio: [audioVerdict, audio],
    screen: [screenVerdict, screen],
    video: [videoVerdict, []],
    ai: [aiVerdict, ai],
  };
  return {
    events: ev,
    monitored: monitored(S),
    passages: aiPassages(ev),
    segments: SEGMENT_NAMES.map((s) => ({ k: s.k, n: s.n, verdict: segs[s.k][0], flags: segs[s.k][1].sort(byTime) })),
    flags: all,
    verdict: all.length ? `${count(all.length, "thing", "things")} to review` : "Nothing flagged",
    clean: !all.length,
  };
}

/**
 * The question on screen at a moment of the round ("question 4"), the questions being sat one after another.
 * @param {Array<{i: number, secs: number}>} qs
 * @param {number} at
 * @returns {string|null}
 */
export function questionAt(qs, at) {
  let from = 0;
  for (const q of qs) {
    if (at >= from && at < from + q.secs) return `question ${q.i}`;
    from += q.secs;
  }
  return null;
}

/* ---- the feed: every instance of the round in time order, with its severity ---- */

/** The severity of a capture: a match is clear, no face is worth a look, another face or another person is critical. */
export const CAPTURE_SEVERITY = { match: "clear", none: "review", second: "critical", different: "critical" };
/** The severity of a screen event once flagged: sharing, a second display and a screenshot are critical; focus lost is worth a look. */
export const SCREEN_SEVERITY = { shared: "critical", display: "critical", screenshot: "critical", focus: "review" };

/** "question 4" as the events name it, read back to its number. */
const questionOf = (into) => {
  const m = /question (\d+)/.exec(into || "");
  return m ? Number(m[1]) : undefined;
};

/**
 * Every instance of the round, in time order, with its severity by kind. The flagged ones carry the flag's own words.
 * @returns {FeedItem[]}
 */
export function proctorFeed(p) {
  const E = p.events;
  const flagOf = (kind, index) => p.flags.find((f) => f.ref && f.ref.kind === kind && f.ref.index === index) || null;
  const out = [];
  const push = (kind, at, severity, what, extra = {}) => {
    const id = `${kind}-${at}-${out.length}`;
    out.push({ id, key: id, at, kind, seg: KIND_SEG[kind], severity, flagged: severity !== "clear", what, detail: null, ...extra });
  };

  const matched = E.captures.filter((c) => c.result === "match").length;
  if (E.settings.image) push("capture", 0, "clear", "Reference image", { capture: "reference", detail: "Taken before the round; every capture is matched against it" });
  E.captures.forEach((c) => push("capture", c.at, E.settings.image ? CAPTURE_SEVERITY[c.result] : "clear",
    c.result === "match" ? "Face matched" : CAPTURE_WORD[c.result], { capture: c.result }));
  E.tabs.forEach((t, i) => {
    const f = flagOf("tab", i);
    push("tab", t.at, f ? (t.away > LONG_AWAY ? "critical" : "review") : "clear", f ? f.what : `Tab switch ${i + 1}, away ${span(t.away)}`, { dur: t.away });
  });
  E.fullScreen.forEach((x, i) => {
    const f = flagOf("fullscreen", i);
    push("fullscreen", x.at, f ? "review" : "clear", f ? f.what : `Left full screen for ${span(x.back)}`, { dur: x.back });
  });
  E.clipboard.forEach((c, i) => {
    const f = flagOf("clipboard", i);
    push("clipboard", c.at, f ? "review" : "clear", f ? f.what : `A ${c.kind} attempt blocked${i < CLIP_ALLOWED ? ", let pass" : ""}`,
      { detail: `In ${c.into}`, question: questionOf(c.into) });
  });
  if (E.settings.video) {
    push("video", 0, "clear", "Recording started", { detail: "The camera and the screen, on one clock" });
    push("video", E.length, "clear", `Recording ended after ${span(E.length)}, no gaps`,
      { detail: E.captures.length ? `The face stayed in frame in ${matched} of ${E.captures.length} captures` : "No captures were taken" });
  }
  E.audio.forEach((a, i) => {
    const f = flagOf("audio", i);
    push("audio", a.at, f ? (a.kind === "voice" ? "critical" : "review") : "clear",
      f ? f.what : `${AUDIO_WORD[a.kind]} for ${span(a.dur)}, too short to flag`, { dur: a.dur });
  });
  E.screen.forEach((e, i) => {
    const f = flagOf("screen", i);
    push("screen", e.at, f ? SCREEN_SEVERITY[e.kind] : "clear", f ? f.what : `${SCREEN_WORD[e.kind]} for ${span(e.dur || 0)}`, { dur: e.dur });
  });
  /* the response timing pattern: each stretch of writing; the flagged ones say why they did not look typed, and
     their detail is only where the writing went (the figures are already in the flag's words) */
  E.typing.forEach((t, i) => {
    const f = flagOf("typing", i);
    push("typing", t.at, f ? "critical" : "clear", f ? f.what : `Typed ${t.chars} characters in ${span(t.secs)} from ${t.keys} keystrokes, as typing is`,
      { dur: t.secs, detail: f ? `In ${t.into}` : `In ${t.into}: ${t.chars} characters, ${span(t.secs)}, ${t.keys} keystrokes`, question: questionOf(t.into) });
  });
  /* AI-style phrasing: each passage likely written with help, with the text and why it reads as generated */
  p.passages.forEach((x, i) => {
    if (!x.likely) return;
    const f = flagOf("passage", i);
    const burst = x.burst ? `; it also ${x.burst.why.charAt(0).toLowerCase()}${x.burst.why.slice(1)}` : "";
    push("passage", E.length, "critical", f ? f.what : `Paragraph ${x.i + 1} likely written with AI help (${Math.round(x.likelihood * 100)} in 100)`,
      { detail: `Reads as generated: ${Math.round(x.likelihood * 100)} in 100 likelihood of AI help${burst}. “${x.text}”` });
  });
  return out.sort((a, b) => a.at - b.at || KIND_ORDER[a.kind] - KIND_ORDER[b.kind]);
}

/* ---- the figures and the words over a feed ---- */

/** Whether an item counts toward the figures: the recording's own items and experimental ones do not. */
export const isCounted = (x) => x.kind !== "video" && !x.experimental;

/**
 * How many counted instances of each severity a feed holds.
 * @param {FeedItem[]} items
 * @returns {{clear: number, review: number, critical: number}}
 */
export function feedCounts(items) {
  const n = { clear: 0, review: 0, critical: 0 };
  items.filter(isCounted).forEach((x) => { n[x.severity] += 1; });
  return n;
}

/**
 * The integrity figure: the share of counted instances that are clear, 100 when nothing was counted.
 * @param {FeedItem[]} items
 * @returns {number}
 */
export function integrityOf(items) {
  const counted = items.filter(isCounted);
  return counted.length ? Math.round((100 * feedCounts(counted).clear) / counted.length) : 100;
}

/**
 * The integrity ring's band.
 * @param {number} integrity
 * @returns {"good"|"mid"|"low"}
 */
export const ringBand = (integrity) => (integrity >= 90 ? "good" : integrity >= 70 ? "mid" : "low");

/** "A.", "A and b.", "A, b, and c.": the phrases as one sentence, capitalised. */
export function sentenceOf(phrases) {
  if (!phrases.length) return "";
  const cap = (s) => `${s.charAt(0).toUpperCase()}${s.slice(1)}`;
  if (phrases.length === 1) return `${cap(phrases[0])}.`;
  if (phrases.length === 2) return `${cap(phrases[0])} and ${phrases[1]}.`;
  return `${cap(phrases.slice(0, -1).join(", "))}, and ${phrases[phrases.length - 1]}.`;
}

const AWAY_CAPTURES = ["none", "noPerson", "distracted"];
const SECOND_CAPTURES = ["second", "anotherPerson", "collaboration"];
const DIFFERENT_CAPTURES = ["different", "differentPerson"];

/**
 * What the flags add up to, in a line: what was seen.
 * @param {FeedItem[]} items  the flagged items (unflagged and recording items are ignored)
 * @returns {string}
 */
export function narrate(items) {
  const flagged = items.filter((x) => x.flagged && x.kind !== "video");
  if (!flagged.length) return "Every check came back clear.";
  const of = (k) => flagged.filter((x) => x.kind === k);
  const times = (n) => (n === 1 ? "" : n === 2 ? " twice" : ` ${n} times`);
  const caps = of("capture");
  const capIn = (list) => caps.some((x) => list.includes(x.capture));
  const away = caps.filter((x) => AWAY_CAPTURES.includes(x.capture)).length;
  const screen = of("screen");
  const integrity = of("integrity");
  const seen = [
    away && `looked away${times(away)}`,
    capIn(SECOND_CAPTURES) && "another person in view",
    capIn(DIFFERENT_CAPTURES) && "someone else on camera",
    capIn(["unauthorizedDevice"]) && "a device in view",
    capIn(["tampering"]) && "the camera blocked",
    capIn(["unverified"]) && "flagged snapshots",
    of("profile").length && "a failed profile check",
    of("audio").some((x) => x.severity === "critical") && "possible outside help",
    of("audio").some((x) => x.severity !== "critical") && "speech outside answers",
    of("quote").length && "moments flagged by the audio review",
    screen.some((x) => /display/i.test(x.what)) && "a second display",
    screen.some((x) => /shared/i.test(x.what)) && "screen sharing",
    screen.some((x) => !/display|shared/i.test(x.what)) && "something on screen",
    (of("typing").length || of("passage").length) && "AI-written text",
    integrity.some((x) => (x.signals || []).includes("aiGenerated")) && "answers that may be AI-written",
    integrity.some((x) => (x.signals || []).includes("readAloud")) && "answers that may be read aloud",
    (of("tab").length || of("tabcount").length) && "tab switches",
    of("session").length && (of("session").length === 1 ? "a session restart" : `${of("session").length} session restarts`),
    of("fullscreen").length && "left full screen",
    of("clipboard").length && "copy attempts",
  ].filter(Boolean);
  return sentenceOf(seen);
}

/**
 * The room's headline: the verdict word and the line under it.
 * @param {FeedItem[]} items  the whole feed
 */
export function reviewHeadline(items) {
  const flagged = items.filter((x) => x.flagged && x.kind !== "video");
  const critical = flagged.filter((x) => x.severity === "critical").length;
  const verdict = !flagged.length ? "Clean session" : critical ? "Needs review" : "Worth a look";
  return { verdict, line: narrate(flagged), flagged: flagged.length, critical };
}
